/**
 * Pins the SHORTER of the product page's two columns (image gallery vs.
 * info/form) on desktop, and animates a `--sticky-top` CSS variable so the
 * pinned column slides in sync with the (possibly hiding) header.
 *
 * Public surface preserved from the previous build:
 *   - Custom element name:   <product-sticky>
 *   - Class toggled:         is-sticky (on the chosen column)
 *   - CSS variable written:  --sticky-top (on the chosen column)
 *   - Global flag:           theme.variables.productPageSticky
 *   - Events listened:       theme:scroll, theme:resize, theme:form:sticky
 */
(() => {
  'use strict';

  const PRODUCT_PAGE_SELECTOR = '.product__page';
  const FORM_WRAPPER_SELECTOR = '[data-form-wrapper]';
  const HEADER_STICKY_SELECTOR = '[data-header-sticky]';
  const MEDIA_LIST_SELECTOR = '[data-product-media-list]';
  const MEDIA_WRAPPER_SELECTOR = 'product-images';
  const STICKY_CLASS = 'is-sticky';
  const STICKY_CSS_VAR = '--sticky-top';
  const DEFAULT_SPACING = 30;

  if (customElements.get('product-sticky')) return;

  /**
   * @classdesc Two-column product page sticky controller. Picks whichever
   * column is shorter as the sticky target so the user always keeps the
   * shorter column in view while scrolling past the taller one.
   * @extends HTMLElement
   */
  class ProductSticky extends HTMLElement {
    constructor() {
      super();

      this.formWrapper = this.querySelector(FORM_WRAPPER_SELECTOR);
      this.mediaWrapper = this.querySelector(MEDIA_WRAPPER_SELECTOR);
      this.mediaList = this.querySelector(MEDIA_LIST_SELECTOR);

      /** @type {HTMLElement|null} The element currently carrying .is-sticky. */
      this.stickyTarget = null;

      this.stickyScrollTop = 0;
      this.scrollLastPosition = 0;
      this.stickyDefaultTop = 0;
      this.currentPoint = 0;
      this.defaultTopBottomSpacings = DEFAULT_SPACING;
      this.scrollTopPosition = window.scrollY;
      this.scrollDirectionDown = true;
      this.requestAnimationSticky = null;
      this.stickyFormLoad = true;
      this.stickyFormLastHeight = null;
      this.onChangeCounter = 0;

      this.scrollEvent = (event) => this.scrollEvents(event);
      this.resizeEvent = (event) => this.resizeEvents(event);
      this.stickyFormEvent = (event) => this.stickyFormEvents(event);
    }

    connectedCallback() {
      this.stickyScrollCheck();
      document.addEventListener('theme:resize', this.resizeEvent);

      if (theme.variables.productPageSticky) {
        this.#attachStickyListeners();
        this.requestAnimationSticky = requestAnimationFrame(() =>
          this.calculateStickyPosition()
        );
      }
    }

    disconnectedCallback() {
      document.removeEventListener('theme:resize', this.resizeEvent);
      this.#detachStickyListeners();
      this.removeAnimationFrameSticky();
    }

    /**
     * Decides which column (if any) should be pinned right now.
     * The choice is merchant-driven via `data-sticky-target` on the wrapper
     * (`info` -> form column, `images` -> gallery). Mobile and missing target
     * elements both fall back to "no sticky".
     * @returns {HTMLElement|null}
     */
    pickStickyTarget() {
      if (window.theme.isMobile()) return null;

      const scope = this.querySelector(PRODUCT_PAGE_SELECTOR);
      if (!scope) return null;

      const targetPref = this.dataset.stickyTarget === 'images' ? 'images' : 'info';

      if (targetPref === 'images') {
        const mediaWrapper = scope.querySelector(MEDIA_WRAPPER_SELECTOR);
        const mediaList = scope.querySelector(MEDIA_LIST_SELECTOR);
        if (!mediaWrapper || !mediaList) return null;
        return mediaWrapper;
      }

      return scope.querySelector(FORM_WRAPPER_SELECTOR) || null;
    }

    /**
     * Re-evaluates which column should be sticky, applies the class to it,
     * removes the class (and stale --sticky-top) from the other, and updates
     * the global flag. Resets the animation baseline if the target changed.
     */
    stickyScrollCheck() {
      this.formWrapper = this.querySelector(FORM_WRAPPER_SELECTOR);
      this.mediaWrapper = this.querySelector(MEDIA_WRAPPER_SELECTOR);
      this.mediaList = this.querySelector(MEDIA_LIST_SELECTOR);

      const previousTarget = this.stickyTarget;
      const nextTarget = this.pickStickyTarget();

      const candidates = [this.formWrapper, this.mediaWrapper];
      for (const candidate of candidates) {
        if (!candidate || candidate === nextTarget) continue;
        candidate.classList.remove(STICKY_CLASS);
        candidate.style.removeProperty(STICKY_CSS_VAR);
      }

      if (nextTarget) {
        nextTarget.classList.add(STICKY_CLASS);
      }

      this.stickyTarget = nextTarget;
      theme.variables.productPageSticky = Boolean(nextTarget);

      if (nextTarget !== previousTarget) {
        this.stickyFormLoad = true;
        this.currentPoint = 0;
        this.stickyScrollTop = 0;
        this.stickyFormLastHeight = null;
      }
    }

    /**
     * Scroll handler (driven by the theme's debounced theme:scroll event).
     * Captures the latest position/direction and schedules a paint.
     * @param {CustomEvent} event
     */
    scrollEvents(event) {
      this.scrollTopPosition = event.detail.position;
      this.scrollDirectionDown = event.detail.down;

      if (this.requestAnimationSticky) return;
      this.requestAnimationSticky = requestAnimationFrame(() =>
        this.calculateStickyPosition()
      );
    }

    /**
     * Form height change (e.g. accordion toggle). Re-pick the sticky target
     * because the in-form change may have flipped which column is shorter.
     * @param {CustomEvent} event
     */
    stickyFormEvents(event) {
      this.stickyScrollCheck();
      this.removeAnimationFrameSticky();

      if (!theme.variables.productPageSticky) {
        this.#detachStickyListeners();
        return;
      }

      this.requestAnimationSticky = requestAnimationFrame(() =>
        this.calculateStickyPosition(event)
      );
    }

    /**
     * Resize handler. Re-evaluates the sticky target and attaches/detaches
     * scroll/form listeners depending on whether sticky is now active.
     */
    resizeEvents() {
      const wasSticky = theme.variables.productPageSticky;
      this.stickyScrollCheck();
      const isSticky = theme.variables.productPageSticky;

      if (wasSticky && !isSticky) {
        this.#detachStickyListeners();
        this.removeAnimationFrameSticky();
        return;
      }

      if (!wasSticky && isSticky) {
        this.#attachStickyListeners();
        this.requestAnimationSticky = requestAnimationFrame(() =>
          this.calculateStickyPosition()
        );
      }
    }

    /**
     * Per-frame sticky math. Updates --sticky-top on the active target so it
     * eases toward a position that:
     *   - sits below the (possibly hiding) header on first paint, and
     *   - stays clamped between "header offset" and "viewport bottom minus
     *     target height minus 30px" so the bottom of the target is reachable.
     * @param {CustomEvent|null} [event]
     */
    calculateStickyPosition(event = null) {
      if (document.documentElement.hasAttribute('data-scroll-locked')) {
        this.removeAnimationFrameSticky();
        return;
      }

      if (!this.stickyTarget) {
        this.removeAnimationFrameSticky();
        return;
      }

      const fromCustomEvent = Boolean(event && event.detail);
      const isAccordionEvent = Boolean(
        fromCustomEvent && event.detail.element && event.detail.element === 'accordion'
      );

      const targetHeight = this.stickyTarget.offsetHeight;
      const stickyMin = window.innerHeight - targetHeight - this.defaultTopBottomSpacings;
      const scrollDelta = Math.abs(this.scrollTopPosition - this.scrollLastPosition);

      if (this.scrollDirectionDown) {
        this.stickyScrollTop -= scrollDelta;
      } else {
        this.stickyScrollTop += scrollDelta;
      }

      if (this.stickyFormLoad) {
        if (document.querySelector(HEADER_STICKY_SELECTOR)) {
          const { headerHeight } = window.theme.readHeights();
          this.stickyDefaultTop = headerHeight;
        } else {
          this.stickyDefaultTop = this.defaultTopBottomSpacings;
        }
        this.stickyScrollTop = this.stickyDefaultTop;
      }

      this.stickyScrollTop = Math.min(
        Math.max(this.stickyScrollTop, stickyMin),
        this.stickyDefaultTop
      );

      const distance = this.stickyScrollTop - this.currentPoint;
      this.currentPoint = this.stickyFormLoad
        ? this.stickyScrollTop
        : this.currentPoint + 0.5 * distance;

      this.stickyTarget.style.setProperty(STICKY_CSS_VAR, `${this.currentPoint}px`);
      this.scrollLastPosition = this.scrollTopPosition;
      this.stickyFormLoad = false;

      const accordionStillSettling = isAccordionEvent && this.onChangeCounter <= 10;
      const accordionHeightChanged =
        isAccordionEvent && this.stickyFormLastHeight !== targetHeight;
      const stillEasingToTarget =
        this.stickyScrollTop !== this.currentPoint && this.requestAnimationSticky;

      if (accordionStillSettling || accordionHeightChanged || stillEasingToTarget) {
        if (isAccordionEvent) this.onChangeCounter += 1;
        if (accordionHeightChanged) this.onChangeCounter = 11;
        this.requestAnimationSticky = requestAnimationFrame(() =>
          this.calculateStickyPosition(event)
        );
      } else if (this.requestAnimationSticky) {
        this.removeAnimationFrameSticky();
      }

      this.stickyFormLastHeight = targetHeight;
    }

    removeAnimationFrameSticky() {
      if (!this.requestAnimationSticky) return;
      cancelAnimationFrame(this.requestAnimationSticky);
      this.requestAnimationSticky = null;
      this.onChangeCounter = 0;
    }

    /**
     * Form-bubbled height events (accordions etc.) live inside the form
     * column, so we listen on the form wrapper regardless of which column is
     * currently the sticky target.
     */
    #attachStickyListeners() {
      document.addEventListener('theme:scroll', this.scrollEvent);
      if (this.formWrapper) {
        this.formWrapper.addEventListener('theme:form:sticky', this.stickyFormEvent);
      }
    }

    #detachStickyListeners() {
      document.removeEventListener('theme:scroll', this.scrollEvent);
      if (this.formWrapper) {
        this.formWrapper.removeEventListener('theme:form:sticky', this.stickyFormEvent);
      }
    }
  }

  customElements.define('product-sticky', ProductSticky);
})();
